/**
 * § Primitives, Kbd: the platform's key glyphs and a shortcut written as text, apart from `Kbd.tsx`
 * so a control that names its shortcut in words (`Button`'s `shortcut`, 2026-09-28) carries the
 * tables alone and not the caps' animation library.
 */
export type Platform = "mac" | "other";

const GLYPHS: Readonly<Record<Platform, Readonly<Record<string, string>>>> = {
  mac: { Mod: "⌘", Cmd: "⌘", Meta: "⌘", Ctrl: "⌃", Alt: "⌥", Option: "⌥", Shift: "⇧", Enter: "↩", Backspace: "⌫", Delete: "⌦", Esc: "⎋", Tab: "⇥", Up: "↑", Down: "↓", Left: "←", Right: "→" },
  other: { Mod: "Ctrl", Cmd: "Ctrl", Meta: "Win", Ctrl: "Ctrl", Alt: "Alt", Option: "Alt", Shift: "Shift", Enter: "Enter", Backspace: "Backspace", Delete: "Del", Esc: "Esc", Tab: "Tab", Up: "↑", Down: "↓", Left: "←", Right: "→" },
};

export function detectPlatform(userAgent: string): Platform {
  return /Mac|iPhone|iPad|iPod/.test(userAgent) ? "mac" : "other";
}

export function glyphFor(key: string, platform: Platform): string {
  return GLYPHS[platform][key] ?? key;
}

/** A letter is shown upper case, as a keyboard prints it (§ Key hints, 2026-10-15); every other key keeps its spelling. */
function printed(name: string): string {
  return /^[a-z]$/.test(name) ? name.toUpperCase() : name;
}

/**
 * A shortcut as one run of text, for a control that names its own key inside itself (a button's
 * "Create client ⌘↩", 2026-09-28): the platform's glyphs with no caps, a sequence's steps apart,
 * and "+" between the keys of a chord where the platform writes words ("Ctrl+Enter") but not
 * between glyphs ("⌘↩"). Since 2026-10-15 (§ Key hints) a letter is upper case, and the steps of
 * a sequence are joined by `then`, the word "then" in the reader's language ("G then I"), where the
 * host gives one; with none they stand a space apart, as the reference dialog's caps do.
 */
export function shortcutText(keys: string, platform: Platform, then?: string): string {
  return keys
    .split(" ")
    .map((chord) =>
      chord
        .split("+")
        .filter((name) => name.length > 0)
        .map((name) => printed(glyphFor(name, platform)))
        .join(platform === "mac" ? "" : "+"),
    )
    .join(then === undefined ? " " : ` ${then} `);
}

/**
 * A shortcut spelled out for assistive technology, which hears words rather than glyphs: the keys by
 * their names with "+" between the keys of a chord and `then` between the steps of a sequence ("G then
 * I", "Ctrl+Enter"), `Mod` named as the platform's own modifier.
 */
export function shortcutSpoken(keys: string, then: string): string {
  return shortcutText(keys, "other", then);
}

/** The WAI ARIA `aria-keyshortcuts` value of a shortcut: `Mod` is the command key on Apple platforms and Control elsewhere, so it names both. */
export function ariaKeyShortcuts(keys: string): string {
  const name = (key: string) => (key === "Esc" ? "Escape" : key === "Up" || key === "Down" || key === "Left" || key === "Right" ? `Arrow${key}` : printed(key));
  const chords = keys.split(" ");
  if (chords.length > 1) return chords.map((chord) => chord.split("+").map(name).join("+")).join(" ");
  const parts = keys.split("+").map(name);
  return parts.includes("Mod") ? ["Meta", "Control"].map((modifier) => parts.map((part) => (part === "Mod" ? modifier : part)).join("+")).join(" ") : parts.join("+");
}
